import React from 'react';
import { Link as LinkR } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { FaBars, FaTimes } from "react-icons/fa";
import { Bio } from '../../data/constants';
import { DarkMode, LightMode } from '@mui/icons-material';

const ThemeToggleButton = styled.button`
  background: ${({ theme }) => theme.glass};
  border: 1px solid ${({ theme }) => theme.glass_border};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.primary};
  padding: 8px;
  border-radius: 50%;
  transition: all 0.4s ease;
  
  &:hover {
    background: ${({ theme }) => theme.primary + '20'};
    box-shadow: 0 0 20px ${({ theme }) => theme.shadow_primary};
    transform: rotate(180deg) scale(1.1);
    border-color: ${({ theme }) => theme.primary};
  }
  
  @media screen and (max-width: 768px) {
    display: none;
  }
`;

const Nav = styled.nav`
  background: ${({ theme }) => theme.glass};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid ${({ theme }) => theme.glass_border};
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1rem;
  position: sticky;
  top: 0;
  z-index: 100;
  transition: all 0.3s ease;
`;

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  height: 60px;
  z-index: 1;
  width: 100%;
  padding: 0 24px;
  max-width: 1200px;
`;

const NavLogo = styled(LinkR)`
  width: 80%;
  padding: 0 6px;
  display: flex;
  justify-content: flex-start;
  cursor: pointer;
  text-decoration: none;
  align-items: center;
  
  @media screen and (max-width: 648px) {
    padding: 0 0px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
`;

const LogoText = styled.span`
  font-weight: 700;
  font-size: 22px;
  background: ${({ theme }) => theme.gradient_text};
  background-size: 200% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  
  &:hover {
    filter: drop-shadow(0 0 8px ${({ theme }) => theme.shadow_primary});
  }
  
  @media screen and (max-width: 640px) {
    font-size: 18px;
  }
`;

const MobileIcon = styled.div`
  display: none;
  @media screen and (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    cursor: pointer;
    color: ${({ theme }) => theme.text_primary};
    transition: all 0.3s ease;
    
    &:hover {
      color: ${({ theme }) => theme.primary};
    }
  }
`;

const NavItems = styled.ul`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 32px;
  list-style: none;
  
  @media screen and (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled.a`
  color: ${({ theme }) => theme.text_primary};
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  padding: 8px 0;
  
  &::before {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: 0;
    left: 50%;
    background: ${({ theme }) => theme.gradient_primary};
    transition: all 0.3s ease;
    transform: translateX(-50%);
    border-radius: 2px;
  }
  
  &:hover {
    color: ${({ theme }) => theme.primary};
    text-shadow: 0 0 10px ${({ theme }) => theme.shadow_primary};
  }
  
  &:hover::before {
    width: 100%;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 80%;
  height: 100%;
  padding: 0 6px;
  gap: 16px;
  
  @media screen and (max-width: 640px) {
    display: none;
  }
`;

const GitHubButton = styled.a`
  text-decoration: none;
  background: transparent;
  color: ${({ theme }) => theme.primary};
  border: 2px solid ${({ theme }) => theme.primary};
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  height: 42px;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  letter-spacing: 0.5px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.gradient_primary};
    transition: left 0.4s ease;
    z-index: -1;
  }
  
  &:hover {
    color: ${({ theme }) => theme.bg};
    border-color: transparent;
    transform: translateY(-2px);
    box-shadow: 0 10px 30px ${({ theme }) => theme.shadow_primary};
  }
  
  &:hover::before {
    left: 0;
  }
  
  @media screen and (max-width: 640px) {
    font-size: 12px;
    padding: 8px 16px;
  }
`;

const MobileMenu = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 16px;
  position: fixed;
  top: 80px;
  right: 0;
  width: 100%;
  height: calc(100vh - 80px);
  padding: 24px 40px;
  background: ${({ theme }) => theme.card + 'f5'};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transition: all 0.3s ease;
  transform: ${({ open }) => open ? 'translateX(0)' : 'translateX(100%)'};
  border-top: 1px solid ${({ theme }) => theme.glass_border};
  z-index: 99;
`;

const MobileMenuLinks = styled.a`
  color: ${({ theme }) => theme.text_primary};
  font-weight: 500;
  font-size: 18px;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.glass_border};
  
  &:hover {
    color: ${({ theme }) => theme.primary};
    padding-left: 16px;
    border-color: ${({ theme }) => theme.primary};
  }
`;

const MobileThemeToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.glass_border};
`;

const Navbar = ({ darkMode, toggleTheme }) => {
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();

  return (
    <Nav>
      <NavContainer>
        <NavLogo to="/">
          <LogoContainer>
            <LogoText>Sunny Mondal</LogoText>
          </LogoContainer>
        </NavLogo>

        <MobileIcon onClick={() => setOpen(!open)}>
          {open ? <FaTimes /> : <FaBars />}
        </MobileIcon>

        <NavItems>
          <NavLink href='#about'>About</NavLink>
          <NavLink href='#skills'>Skills</NavLink>
          <NavLink href='#experience'>Experience</NavLink>
          <NavLink href='#projects'>Projects</NavLink>
          <NavLink href='#education'>Education</NavLink>
        </NavItems>

        <ButtonContainer>
          <ThemeToggleButton onClick={toggleTheme} aria-label="Toggle theme">
            {darkMode ? <LightMode /> : <DarkMode />}
          </ThemeToggleButton>
          <GitHubButton href={Bio.github} target="_blank">Github Profile</GitHubButton>
        </ButtonContainer>
      </NavContainer>

      <MobileMenu open={open}>
        <MobileMenuLinks href='#about' onClick={() => setOpen(false)}>About</MobileMenuLinks>
        <MobileMenuLinks href='#skills' onClick={() => setOpen(false)}>Skills</MobileMenuLinks>
        <MobileMenuLinks href='#experience' onClick={() => setOpen(false)}>Experience</MobileMenuLinks>
        <MobileMenuLinks href='#projects' onClick={() => setOpen(false)}>Projects</MobileMenuLinks>
        <MobileMenuLinks href='#education' onClick={() => setOpen(false)}>Education</MobileMenuLinks>

        <MobileThemeToggle>
          <span style={{ color: theme.text_secondary }}>
            {darkMode ? 'Switch to Light' : 'Switch to Dark'}
          </span>
          <ThemeToggleButton
            onClick={toggleTheme}
            aria-label="Toggle theme"
            style={{ display: 'flex' }}
          >
            {darkMode ? <LightMode /> : <DarkMode />}
          </ThemeToggleButton>
        </MobileThemeToggle>

        <GitHubButton
          style={{
            marginTop: '16px',
            width: '100%',
            justifyContent: 'center',
          }}
          href={Bio.github}
          target="_blank"
        >
          Github Profile
        </GitHubButton>
      </MobileMenu>
    </Nav>
  );
};

export default Navbar;
